import _ from "lodash";
import cn from "classnames";
import moment, { Moment } from "moment";
import React, { useEffect, useState } from "react";
import { Col, Row } from "reactstrap";
import { PanelWembii } from "../PanelWembii";
import { DAY_OF_WEEK, DAY_OF_WEEK_SHORT } from "../../utils/dateUtils";
import { useSocialPostCore } from "../../../cores/socialPost";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { PostItem } from "./PostItem";
import { SocialPost } from "../../../cores/socialPost/interfaces/api/get-social-posts";
import { getSocialPostCalendarMoment } from "./socialPostCalendarMoment";

type CalendarView = "day" | "week" | "month";

/** Orden cronológico por fecha/hora (publicado o programado). */
function sortPostsByScheduleTime(posts: SocialPost[]): SocialPost[] {
  return [...posts].sort((a, b) => {
    const ma = getSocialPostCalendarMoment(a);
    const mb = getSocialPostCalendarMoment(b);
    const ta = ma.isValid() ? ma.valueOf() : 0;
    const tb = mb.isValid() ? mb.valueOf() : 0;
    if (ta !== tb) return ta - tb;
    return String(a.post.id).localeCompare(String(b.post.id));
  });
}

interface Props {
  view?: CalendarView;
  bestHoursNetwork?: "none" | "facebook" | "instagram" | "linkedin" | "youtube";
  showBestHoursPercent?: boolean;
  onCreatePostAt?: (day: Moment, hour?: number) => void;
  onOpenPostComposer?: (post: SocialPost) => void;
  onReschedulePost?: (post: SocialPost, nextMoment: Moment) => void;
}

/** En vista mes/día/semana: si hay más publicaciones, se muestran las primeras N y un control para expandir. */
const MAX_VISIBLE_POSTS_PER_CELL = 3;

const BEST_HOURS_COLORS: Record<"facebook" | "instagram" | "linkedin" | "youtube", string> = {
  facebook: "46,107,214",
  instagram: "226,79,140",
  linkedin: "32,124,184",
  youtube: "255,80,80",
};

const NETWORK_WEEKDAY_BASE: Record<
  "facebook" | "instagram" | "linkedin" | "youtube",
  number[]
> = {
  facebook: [28, 42, 56, 60, 52, 35, 31],
  instagram: [22, 40, 58, 66, 63, 48, 37],
  linkedin: [18, 32, 54, 62, 67, 45, 24],
  youtube: [35, 48, 62, 70, 68, 55, 40],
};

interface DayCellPostsProps {
  posts: SocialPost[];
  day: Moment;
  /** Vista semanal: hora de la celda para programar en esa franja. */
  hour?: number;
  onOpenPostComposer?: (post: SocialPost) => void;
  onCreatePostAt?: (day: Moment, hour?: number) => void;
  draggableForPost: (post: SocialPost) => boolean;
}

const DayCellPosts = ({
  posts,
  day,
  hour,
  onOpenPostComposer,
  onCreatePostAt,
  draggableForPost,
}: DayCellPostsProps) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (posts.length <= MAX_VISIBLE_POSTS_PER_CELL) setExpanded(false);
  }, [posts.length]);

  const needsCollapse = posts.length > MAX_VISIBLE_POSTS_PER_CELL;
  const hiddenCount = posts.length - MAX_VISIBLE_POSTS_PER_CELL;
  const shown =
    expanded || !needsCollapse
      ? posts
      : posts.slice(0, MAX_VISIBLE_POSTS_PER_CELL);

  /** "+" cuando hay 2+ publicaciones en la celda (antes exigía 3 por error al usar MAX_VISIBLE). */
  const showAddPostButton =
    Boolean(onCreatePostAt) && posts.length >= 2;

  return (
    <>
      {shown.map((post) => (
        <PostItem
          post={post}
          key={post.post.id}
          onOpenPostComposer={onOpenPostComposer}
          draggable={draggableForPost(post)}
        />
      ))}
      {needsCollapse && (
        <button
          type="button"
          className="calendar-cell-posts__toggle"
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setExpanded((v) => !v);
          }}
        >
          {expanded ? "Ver menos" : `Ver ${hiddenCount} más`}
        </button>
      )}
      {showAddPostButton && (
        <button
          type="button"
          className="calendar-cell-posts__add"
          title="Agregar publicación"
          aria-label="Agregar publicación en este día"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onCreatePostAt?.(moment(day), hour);
          }}
        >
          <span className="calendar-cell-posts__add-inner">
            <FontAwesomeIcon icon={faPlus} />
          </span>
        </button>
      )}
    </>
  );
};

export const Calendar = ({
  view = "month",
  bestHoursNetwork = "none",
  showBestHoursPercent = false,
  onCreatePostAt,
  onOpenPostComposer,
  onReschedulePost,
}: Props) => {
  const { date, socialPosts } = useSocialPostCore();

  const getPostsByDate = (d: Moment) =>
    sortPostsByScheduleTime(
      socialPosts.filter((post) => {
        const m = getSocialPostCalendarMoment(post);
        return m.isValid() && d.isSame(m, "day");
      })
    );

  const getPostMoment = (post: SocialPost) => getSocialPostCalendarMoment(post);

  const getPostsByDateAndHour = (day: Moment, hour: number) =>
    sortPostsByScheduleTime(
      socialPosts.filter((post) => {
        const postDate = getPostMoment(post);
        return postDate.isSame(day, "day") && postDate.hour() === hour;
      })
    );

  const renderDays = (startDate: Moment, days = 7) => (
    <Row className="days">
      {_.range(0, days).map((d) => {
        const dow = moment(startDate).add(d, "d").format("d");
        const full = DAY_OF_WEEK[dow];
        return (
          <Col
            className="text-center calendar-day-head px-1 px-sm-2"
            key={d}
            title={full}
          >
            <span className="sr-only d-md-none">{full}</span>
            <span className="calendar-day-head__short d-md-none" aria-hidden>
              {DAY_OF_WEEK_SHORT[dow]}
            </span>
            <span className="calendar-day-head__full d-none d-md-inline">
              {full}
            </span>
          </Col>
        );
      })}
    </Row>
  );

  const getBestHoursScore = (day: Moment, hour?: number): number | null => {
    if (bestHoursNetwork === "none") return null;
    const weekday = Number(day.format("d"));
    const weekOfMonth = Math.max(1, Math.min(5, Math.ceil(day.date() / 7)));
    const weekdayBase = NETWORK_WEEKDAY_BASE[bestHoursNetwork][weekday] || 25;
    const adjustment = (weekOfMonth - 3) * 4 + (day.date() % 3) * 2;
    if (hour === undefined) {
      const score = Math.max(3, Math.min(100, weekdayBase + adjustment));
      return score;
    }
    const peakByNetwork: Record<"facebook" | "instagram" | "linkedin" | "youtube", number> = {
      facebook: 11,
      instagram: 12,
      linkedin: 10,
      youtube: 18,
    };
    const peakHour = peakByNetwork[bestHoursNetwork];
    const hourDistance = Math.abs(hour - peakHour);
    const hourBoost = Math.max(0, 28 - hourDistance * 6);
    const score = Math.max(2, Math.min(100, weekdayBase + adjustment + hourBoost - 12));
    return score;
  };

  const renderBestHoursLayer = (day: Moment, hour?: number) => {
    const score = getBestHoursScore(day, hour);
    if (score === null) return null;
    if (bestHoursNetwork === "none") return null;
    const alpha = Math.max(0.08, Math.min(0.42, score / 200));
    const color = BEST_HOURS_COLORS[bestHoursNetwork];
    return (
      <>
        <div
          className="best-hours-layer"
          style={{ backgroundColor: `rgba(${color}, ${alpha})` }}
        />
        {showBestHoursPercent && (
          <div className="best-hours-label">{score}%</div>
        )}
      </>
    );
  };

  const renderCells = () => {
    if (view === "day") {
      const day = moment(date).startOf("day");
      return (
        <div>
          <Row>
            <Col
              className={cn("cell day-view", {
                today: day.isSame(moment(), "day"),
                "best-hours-active": bestHoursNetwork !== "none",
              })}
                  onClick={() => onCreatePostAt?.(day)}
                  onDragOver={(e) => {
                    if (!onReschedulePost) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => {
                    if (!onReschedulePost) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const postId = e.dataTransfer.getData("text/plain");
                    if (!postId) return;
                    const post = socialPosts.find((p) => String(p.post.id) === String(postId));
                    if (!post) return;
                    const oldMoment = getSocialPostCalendarMoment(post);
                    const next = moment(day)
                      .hour(oldMoment.isValid() ? oldMoment.hour() : 0)
                      .minute(oldMoment.isValid() ? oldMoment.minute() : 0)
                      .second(0)
                      .millisecond(0);
                    onReschedulePost(post, next);
                  }}
            >
              {renderBestHoursLayer(day)}
              <span className="number-day">{day.format("D")}</span>
              <DayCellPosts
                posts={getPostsByDate(day)}
                day={day}
                onCreatePostAt={onCreatePostAt}
                onOpenPostComposer={onOpenPostComposer}
                draggableForPost={(post) =>
                  Boolean(onReschedulePost) && !post.post.postAt
                }
              />
            </Col>
          </Row>
        </div>
      );
    }

    if (view === "week") {
      const weekStart = moment(date).startOf("week");
      const weekDays = _.range(0, 7).map((offset) => moment(weekStart).add(offset, "day"));
      const hours = _.range(5, 24);
      return (
        <div className="calendar-week-grid">
          <div className="calendar-week-grid__header">
            <div className="calendar-week-grid__time-spacer" />
            {weekDays.map((day) => (
              <div
                className={cn("calendar-week-grid__day", {
                  "is-today": day.isSame(moment(), "day"),
                })}
                key={`header-${day.format("YYYY-MM-DD")}`}
              >
                <span className="calendar-week-grid__day-name">
                  <span className="d-md-none">{DAY_OF_WEEK_SHORT[day.format("d")]}</span>
                  <span className="d-none d-md-inline">{DAY_OF_WEEK[day.format("d")]}</span>
                </span>
                <span className="calendar-week-grid__day-number">{day.format("D")}</span>
              </div>
            ))}
          </div>
          <div className="calendar-week-grid__body">
            {hours.map((hour) => (
              <div className="calendar-week-grid__row" key={`hour-${hour}`}>
                <div className="calendar-week-grid__time">{`${hour}:00`}</div>
                {weekDays.map((day) => (
                  <div
                    className={cn("calendar-week-grid__cell", {
                      "is-today": day.isSame(moment(), "day"),
                      "best-hours-active": bestHoursNetwork !== "none",
                    })}
                    key={`${day.format("YYYY-MM-DD")}-${hour}`}
                    onClick={() => onCreatePostAt?.(day, hour)}
                    onDragOver={(e) => {
                      if (!onReschedulePost) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(e) => {
                      if (!onReschedulePost) return;
                      e.preventDefault();
                      e.stopPropagation();
                      const postId = e.dataTransfer.getData("text/plain");
                      if (!postId) return;
                      const post = socialPosts.find((p) => String(p.post.id) === String(postId));
                      if (!post) return;
                      const next = moment(day).hour(hour).minute(0).second(0).millisecond(0);
                      onReschedulePost(post, next);
                    }}
                  >
                    {renderBestHoursLayer(day, hour)}
                    <DayCellPosts
                      posts={getPostsByDateAndHour(day, hour)}
                      day={day}
                      hour={hour}
                      onCreatePostAt={onCreatePostAt}
                      onOpenPostComposer={onOpenPostComposer}
                      draggableForPost={(post) =>
                        Boolean(onReschedulePost) && !post.post.postAt
                      }
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      );
    }

    const monthStart = moment(date).startOf("M");
    const monthEnd = moment(date).endOf("M");
    /* Misma regla que las cabeceras: startOf("week") del locale (p. ej. lunes en es).
       No usar startOf("W"/isoWeek) + isoWeekday(0): desalineaba un día respecto a los títulos. */
    const gridWeekStart = moment(monthStart).startOf("week");

    const rows = [];
    let day = gridWeekStart.clone().subtract(1, "d");

    while (day <= monthEnd) {
      rows.push(
        <Row key={day.toString()}>
          {_.range(0, 7).map(() => {
            day.add(1, "d");
            const currentDay = moment(day);
            const isCurrentMonth = currentDay.isSame(moment(monthStart), "M");

            return (
              <Col 
                className={cn("cell", {
                  "disabled": !isCurrentMonth,
                  "today": currentDay.isSame(moment(), "day"),
                  "best-hours-active":
                    bestHoursNetwork !== "none" &&
                    isCurrentMonth,
                })} 
                key={currentDay.toString()}
                onClick={() =>
                  isCurrentMonth && onCreatePostAt?.(moment(currentDay))
                }
                onDragOver={(e) => {
                  if (!onReschedulePost || !isCurrentMonth) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => {
                  if (!onReschedulePost || !isCurrentMonth) return;
                  e.preventDefault();
                  e.stopPropagation();
                  const postId = e.dataTransfer.getData("text/plain");
                  if (!postId) return;
                  const post = socialPosts.find((p) => String(p.post.id) === String(postId));
                  if (!post) return;
                  const oldMoment = getSocialPostCalendarMoment(post);
                  const next = moment(currentDay)
                    .hour(oldMoment.isValid() ? oldMoment.hour() : 0)
                    .minute(oldMoment.isValid() ? oldMoment.minute() : 0)
                    .second(0)
                    .millisecond(0);
                  onReschedulePost(post, next);
                }}
              >
                {isCurrentMonth && renderBestHoursLayer(currentDay)}
                <span className="number-day">
                  {currentDay.format("D")}
                </span>
                <DayCellPosts
                  posts={getPostsByDate(currentDay)}
                  day={moment(currentDay)}
                  onCreatePostAt={isCurrentMonth ? onCreatePostAt : undefined}
                  onOpenPostComposer={onOpenPostComposer}
                  draggableForPost={(post) =>
                    Boolean(onReschedulePost) && !post.post.postAt
                  }
                />
              </Col>
            );
          })}
        </Row>
      );

    }

    return <div>{rows}</div>;
  };

  return (
    <PanelWembii className="social-calendar-grid w-100 px-3 px-lg-4 py-3 mb-2" shadow rounded="16px">
      <Row>
        <Col xs="12" className="d-flex justify-content-end">
          <div className="calendar w-100">
            {renderDays(
              view === "day"
                ? moment(date).startOf("day")
                : view === "week"
                ? moment(date).startOf("week")
                : moment(date).startOf("month").startOf("week"),
              view === "day" ? 1 : 7
            )}
            {renderCells()}
          </div>
        </Col>
      </Row>
    </PanelWembii>
  );
};
